'use strict';

/**
 * Hover zoom helper for the product images.
 *
 * jquery-zoom ships a "touch fallback" (enabled by default) that binds touchstart/touchmove/
 * touchend on the image and calls preventDefault() on all of them. On a touch screen that
 * swallows the gesture: dragging a finger over the product image pans the magnified image
 * instead of scrolling the page. Because the image fills most of a phone viewport, the page
 * becomes very hard to scroll.
 *
 * The zoom is therefore only wired up for mouse driven (desktop) sessions, and the touch
 * fallback is switched off so hybrid devices (touch screen laptops) keep native gestures too.
 * Touch sessions get `touchImageZoom` instead, which zooms the image on a pinch or a double tap
 * while leaving one finger scrolling alone. The two are mutually exclusive.
 */

var imagesLoaded = require('imagesloaded');
var touchImageZoom = require('./touchImageZoom');

require('jquery-zoom/jquery.zoom.min');

var ZOOM_CONTAINER = '.primary-images .desktop-zoom';

// Matches the breakpoint already used to swap in the high resolution zoom image.
var DESKTOP_MIN_WIDTH = 768;

var mediaQuery = '(hover: hover) and (pointer: fine)';

/**
 * Checks whether the page is driven by a pointer that can hover, i.e. a mouse or a trackpad.
 * Browsers that do not understand the media query report `media === 'not all'`, for those the
 * touch capability of the device is used instead.
 * @returns {boolean} true when the primary input device is a fine pointer that can hover
 */
function hasFinePointer() {
    if (window.matchMedia) {
        var query = window.matchMedia(mediaQuery);

        if (query.media !== 'not all') {
            return query.matches;
        }
    }

    return !('ontouchstart' in window) && !navigator.maxTouchPoints;
}

/**
 * Tells whether the hover zoom applies to the current device and viewport.
 * @returns {boolean} true for desktop sized viewports driven by a mouse
 */
function isHoverZoomSupported() {
    return window.innerWidth > DESKTOP_MIN_WIDTH && hasFinePointer();
}

/**
 * Destroys any zoom previously attached to the container and attaches the one that fits the
 * device: the hover magnifier on desktop, the pinch / double tap gestures on touch screens.
 * @param {string|jQuery} container - element(s) wrapping the product image
 */
function applyZoom(container) {
    var $container = $(container);

    $container.trigger('zoom.destroy');
    $container.find('.zoomImg').remove();

    if (!isHoverZoomSupported()) {
        touchImageZoom.init();
        return;
    }

    touchImageZoom.destroy();
    $container.zoom({ touch: false });
}

/**
 * Waits for the product images to be loaded before (re)initializing the zoom.
 * @param {string|jQuery} [container] - element(s) wrapping the product image
 */
function loadZoom(container) {
    var target = container || ZOOM_CONTAINER;

    imagesLoaded(target).on('always', function () {
        applyZoom(target);
    });
}

var isSupported = isHoverZoomSupported();
var resizeTimeout = null;

// Rotating a tablet or resizing a desktop window can cross the desktop/touch boundary, so the
// zoom is re-evaluated, but only when the outcome actually changed.
$(window).on('resize orientationchange', function () {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(function () {
        if (isHoverZoomSupported() !== isSupported) {
            isSupported = !isSupported;
            loadZoom();
        }
    }, 250);
});

module.exports = {
    isHoverZoomSupported: isHoverZoomSupported,
    applyZoom: applyZoom,
    loadZoom: loadZoom
};
